"use client";

import { useState, useEffect, Suspense, useCallback } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm, useWatch } from "react-hook-form";
import Input from "@/components/ui/Input";
import PasswordInput from "@/components/ui/PasswordInput";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import HierarchicalLocationSelect from "@/components/ui/HierarchicalLocationSelect";
import NormalSelect from "@/components/ui/NormalSelect";
import { useToast } from "@/contexts/ToastContext";
import { parseLocationString } from "@/lib/locationUtils";
import type { ResolvedLocationIds } from "@/lib/locationIds";
import { cn } from "@/lib/utils";
import PasswordStrengthMeter, {
  PasswordCriteria,
} from "@/components/ui/PasswordStrengthMeter";
import TurnstileWidget from "@/components/ui/TurnstileWidget";
import GoogleAuthButton from "@/components/ui/GoogleAuthButton";
import Modal from "@/components/ui/Modal";
import { formatPhoneNumber } from "@/lib/validationUtils";
import {
  signupValidationRules,
  type SignupFormData,
} from "@/lib/formValidation";
import { sanitizeInternalPath } from "@/lib/navigation/authRedirect";
import type { ReferralPartnerSummary } from "@/lib/types";
import { subscribeSignupDevToolboxAdapter } from "@/lib/devToolbox/adapters";
import { nearMeWhatsappSignupDisclosureLine } from "@/lib/notifications/near-me-user-defaults-policy";

function sanitizePartnerCodeInput(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 24);
}

function SignUpContent() {
  const router = useRouter();
  const { isAuthenticated, signup, loginWithGoogle } = useAuth();
  const searchParams = useSearchParams();
  const redirectTo = sanitizeInternalPath(searchParams.get("redirectTo"));
  const referralCode = searchParams.get("ref") || undefined;
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [resolvedPartner, setResolvedPartner] =
    useState<ReferralPartnerSummary | null>(null);
  const [partnerLookupError, setPartnerLookupError] = useState<string | null>(
    null,
  );
  const [isPartnerLookupLoading, setIsPartnerLookupLoading] = useState(false);
  const [lockedReferralCode, setLockedReferralCode] = useState<string | null>(
    null,
  );
  const [isPartnerLocked, setIsPartnerLocked] = useState(false);

  // Add state for location metadata
  const [locationMetadata, setLocationMetadata] = useState<{
    coordinates?: { lat: number; lng: number };
    placeId?: string;
    locationIds?: ResolvedLocationIds;
  }>({});

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors, touchedFields, isValid },
    trigger,
  } = useForm<SignupFormData>({
    mode: "onBlur", // Validate on blur
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "234",
      location: "",
      gender: "",
      discoveryChannel: "",
      partnerReferralCode: "",
    },
  });

  // Watch form values for password strength and form validation
  const watchedPassword = useWatch({ control, name: "password" });
  const watchedConfirmPassword = useWatch({ control, name: "confirmPassword" });
  const watchedName = useWatch({ control, name: "name" });
  const watchedEmail = useWatch({ control, name: "email" });
  const watchedPhone = useWatch({ control, name: "phone" });
  const watchedLocation = useWatch({ control, name: "location" });
  const watchedDiscoveryChannel = useWatch({
    control,
    name: "discoveryChannel",
  });
  const watchedPartnerReferralCode = useWatch({
    control,
    name: "partnerReferralCode",
  });
  const watchedGender = useWatch({ control, name: "gender" });

  // Trigger validation for confirmPassword when password changes
  useEffect(() => {
    if (touchedFields.confirmPassword) {
      trigger("confirmPassword");
    }
  }, [watchedPassword, touchedFields.confirmPassword, trigger]);

  // Handle password strength changes from the component
  const handlePasswordStrengthChange = (
    strength: number,
    _criteria: PasswordCriteria,
  ) => {
    setPasswordStrength(strength);
    // Note: criteria is available but not currently used in validation
  };

  // Use the same getStrengthText function for form validation
  const getPasswordStrengthText = (strength: number) => {
    if (!watchedPassword) return "";
    if (strength === 0 || strength === 1) return "Weak";
    if (strength === 2) return "Fair";
    if (strength === 3) return "Good";
    if (strength === 4) return "Strong";
    return "Very Strong";
  };

  // Calculate if form is valid for button enabling/disabling
  const parsedLocation = parseLocationString(watchedLocation);
  const hasValidLocation =
    parsedLocation.state &&
    parsedLocation.state !== "" &&
    parsedLocation.lga &&
    parsedLocation.lga !== "";
  const hasValidPassword =
    watchedPassword.trim() !== "" &&
    watchedPassword.length >= 6 &&
    watchedConfirmPassword.trim() !== "" &&
    watchedPassword === watchedConfirmPassword &&
    getPasswordStrengthText(passwordStrength) === "Very Strong";
  const hasCaptcha = captchaToken !== undefined;
  const shouldShowPartnerSection =
    watchedDiscoveryChannel === "market_partner" || isPartnerLocked;
  const hasValidPartnerSelection =
    !shouldShowPartnerSection ||
    Boolean(
      resolvedPartner &&
        (isPartnerLocked ||
          sanitizePartnerCodeInput(watchedPartnerReferralCode)),
    );

  const isFormValid =
    isValid &&
    hasValidLocation &&
    hasValidPassword &&
    hasCaptcha &&
    hasValidPartnerSelection;

  // Collect missing/invalid fields for user guidance
  const missingFields: string[] = [];
  if (errors.name) {
    missingFields.push(
      errors.name.message ||
        "Enter your first and last name (2 words, each at least 3 letters)",
    );
  } else if (!watchedName || watchedName.trim() === "") {
    missingFields.push("Enter your first and last name");
  }
  if (errors.email) {
    missingFields.push(errors.email.message || "Enter a valid email address");
  } else if (!watchedEmail || watchedEmail.trim() === "") {
    missingFields.push("Enter your email address");
  }
  if (errors.phone) {
    missingFields.push(
      errors.phone.message || "Enter a valid phone number (234XXXXXXXXX)",
    );
  }
  if (errors.location) {
    missingFields.push(errors.location.message || "Select your state and LGA");
  } else if (!parsedLocation.state || parsedLocation.state === "") {
    missingFields.push("Select your state");
  } else if (!parsedLocation.lga || parsedLocation.lga === "") {
    missingFields.push("Select your LGA (Local Government Area)");
  }
  if (errors.gender) {
    missingFields.push(errors.gender.message || "Select your gender");
  } else if (!watchedGender || watchedGender.trim() === "") {
    missingFields.push("Select your gender");
  }
  if (errors.password) {
    missingFields.push(errors.password.message || "Enter a valid password");
  } else if (!watchedPassword || watchedPassword.trim() === "") {
    missingFields.push("Enter a password");
  } else if (watchedPassword.length < 6) {
    missingFields.push("Password must be at least 6 characters long");
  } else if (getPasswordStrengthText(passwordStrength) !== "Very Strong") {
    missingFields.push("Choose a stronger password (meet all criteria)");
  }
  if (errors.confirmPassword) {
    missingFields.push(
      errors.confirmPassword.message || "Passwords do not match",
    );
  } else if (!watchedConfirmPassword || watchedConfirmPassword.trim() === "") {
    missingFields.push("Confirm your password");
  } else if (watchedPassword && watchedPassword !== watchedConfirmPassword) {
    missingFields.push("Passwords do not match");
  }
  if (!hasCaptcha) {
    missingFields.push("Complete the CAPTCHA verification");
  }
  if (shouldShowPartnerSection && !resolvedPartner) {
    missingFields.push("Enter a valid market partner code");
  }

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      router.push(redirectTo);
    }
  }, [isAuthenticated, redirectTo, router]);

  useEffect(
    () =>
      subscribeSignupDevToolboxAdapter({
        setValue,
        setResolvedPartner,
        setPartnerLookupError,
        setIsPartnerLocked,
        setLockedReferralCode,
      }),
    [setValue],
  );

  const resolvePartnerCode = useCallback(
    async (rawCode: string, options?: { lockOnSuccess?: boolean }) => {
      const code = sanitizePartnerCodeInput(rawCode);
      if (code.length < 10) {
        setResolvedPartner(null);
        setPartnerLookupError(null);
        if (options?.lockOnSuccess) {
          setIsPartnerLocked(false);
          setLockedReferralCode(null);
        }
        return;
      }

      setIsPartnerLookupLoading(true);
      setPartnerLookupError(null);
      try {
        const response = await fetch("/api/referrals/resolve-code", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code }),
        });
        const payload = (await response.json()) as {
          valid?: boolean;
          code?: string;
          partner?: ReferralPartnerSummary;
          error?: string;
        };
        if (!response.ok) {
          setResolvedPartner(null);
          setPartnerLookupError(
            payload.error || "Could not validate partner code",
          );
          if (options?.lockOnSuccess) {
            setIsPartnerLocked(false);
            setLockedReferralCode(null);
          }
          return;
        }
        if (!payload.valid || !payload.partner) {
          setResolvedPartner(null);
          setPartnerLookupError("We couldn't find that market partner code");
          if (options?.lockOnSuccess) {
            setIsPartnerLocked(false);
            setLockedReferralCode(null);
          }
          return;
        }

        setResolvedPartner(payload.partner);
        setPartnerLookupError(null);
        if (options?.lockOnSuccess) {
          setValue("discoveryChannel", "market_partner", {
            shouldValidate: true,
          });
          setValue("partnerReferralCode", payload.code || code, {
            shouldValidate: true,
          });
          setIsPartnerLocked(true);
          setLockedReferralCode(payload.code || code);
        }
      } catch {
        setResolvedPartner(null);
        setPartnerLookupError("Could not validate partner code right now");
        if (options?.lockOnSuccess) {
          setIsPartnerLocked(false);
          setLockedReferralCode(null);
        }
      } finally {
        setIsPartnerLookupLoading(false);
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (!referralCode) return;
    const timer = window.setTimeout(() => {
      void resolvePartnerCode(referralCode, { lockOnSuccess: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [referralCode, resolvePartnerCode]);

  useEffect(() => {
    if (isPartnerLocked) return;
    if (watchedDiscoveryChannel !== "market_partner") return;
    const sanitizedCode = sanitizePartnerCodeInput(watchedPartnerReferralCode);
    if (sanitizedCode.length < 10) return;
    const timer = window.setTimeout(() => {
      void resolvePartnerCode(sanitizedCode);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    watchedPartnerReferralCode,
    watchedDiscoveryChannel,
    isPartnerLocked,
    resolvePartnerCode,
  ]);

  const handleLocationChange = (
    value: string,
    coordinates?: { lat: number; lng: number },
    placeId?: string,
    locationIds?: ResolvedLocationIds,
  ) => {
    console.log("Location changed during signup:", {
      value,
      coordinates,
      placeId,
      locationIds,
    });
    setValue("location", value, { shouldValidate: true });

    setLocationMetadata({ coordinates, placeId, locationIds });
  };

  const onSubmit = async (_data: SignupFormData) => {
    // Validate the form before showing confirmation
    if (!captchaToken) {
      showToast("Please complete the CAPTCHA verification", "error");
      return;
    }

    if (getPasswordStrengthText(passwordStrength) !== "Very Strong") {
      showToast(
        "Please choose a stronger password that meets all criteria",
        "error",
      );
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setIsLoading(true);

    // Get current form values
    const formValues = getValues();

    // Format phone number one more time to ensure it's correct
    const formattedPhone = formatPhoneNumber(formValues.phone);
    const finalFormData = { ...formValues, phone: formattedPhone };

    try {
      console.log("Submitting signup form with data:", {
        name: finalFormData.name,
        email: finalFormData.email,
        password: "***",
        phone: finalFormData.phone,
        location: finalFormData.location,
        gender: finalFormData.gender,
        locationMetadata: locationMetadata,
      });

      const result = await signup(
        finalFormData.name,
        finalFormData.email,
        finalFormData.password,
        captchaToken,
        finalFormData.phone,
        finalFormData.location,
        finalFormData.gender,
        locationMetadata.coordinates,
        locationMetadata.placeId,
        locationMetadata.locationIds,
        finalFormData.discoveryChannel || undefined,
        isPartnerLocked
          ? (lockedReferralCode ?? undefined)
          : finalFormData.discoveryChannel === "market_partner"
            ? sanitizePartnerCodeInput(finalFormData.partnerReferralCode || "")
            : undefined,
      );

      console.log("Signup result:", result);

      if (result.success) {
        if (result.needsVerification) {
          // Show verification required message
          showToast(
            "Account created! Verify your email, then finish near-me alert setup in onboarding.",
            "success",
          );

          // Redirect to verification page
          router.push(
            `/verify-account?email=${encodeURIComponent(finalFormData.email)}&redirectTo=${encodeURIComponent(redirectTo)}`,
          );
        } else {
          // Regular success message (fallback for backward compatibility)
          showToast("Account created successfully!", "success");
          console.log("Signup successful, redirecting to:", redirectTo);
          router.push(redirectTo);
        }
      } else {
        console.error("Signup failed with message:", result.message);
        showToast(result.message, "error");
      }
    } catch (err: unknown) {
      console.error("Signup error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred during signup";
      showToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await loginWithGoogle(redirectTo, referralCode);
      if (!result.success) {
        showToast(result.message, "error");
      }
    } catch (error) {
      console.error("Google signup error:", error);
      showToast("Failed to sign up with Google", "error");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Create your account
          </h1>
          <p className="mt-2 text-sm text-text-light">
            Already have an account?{" "}
            <Link
              href="/login"
              className="font-medium text-primary hover:text-primary-600"
            >
              Log in
            </Link>
          </p>
          <p className="mt-2 text-xs text-text-light">
            After you verify your email, we&apos;ll ask about local listing
            alerts in onboarding — you can adjust them anytime under Profile →
            Notifications.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <GoogleAuthButton
            onClick={handleGoogleSignup}
            isLoading={isGoogleLoading}
            mode="signup"
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-text-light">
                Or continue with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <Input
                id="name"
                type="text"
                autoComplete="name"
                label="First and last name *"
                placeholder="e.g. Mosope Olaiya"
                {...register("name", signupValidationRules.name)}
                error={errors.name?.message}
              />

              <Input
                id="email"
                type="email"
                autoComplete="email"
                label="Email address *"
                placeholder="e.g. mosope@gmail.com"
                {...register("email", signupValidationRules.email)}
                error={errors.email?.message}
              />

              <div>
                {/* WhatsApp Requirement Alert */}
                <div className="mb-3 p-3 bg-[#25D366]/10 border-2 border-[#25D366] rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-[#25D366] mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-bold text-[#25D366] mb-1">
                        Active WhatsApp Number Required
                      </h3>
                      <p className="text-xs text-foreground leading-relaxed">
                        <strong>Important:</strong> We need your active WhatsApp
                        number to approve your item listings. If we cannot reach
                        you on WhatsApp, your items{" "}
                        <strong className="text-red-600">
                          will not be approved
                        </strong>
                        . This ensures we can contact you about potential buyers
                        and verify your listings quickly.
                      </p>
                    </div>
                  </div>
                </div>

                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  label="WhatsApp phone number *"
                  placeholder="e.g. 2348061234567"
                  {...register("phone", {
                    ...signupValidationRules.phone,
                    onChange: (e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setValue("phone", formatted, { shouldValidate: true });
                    },
                  })}
                  error={errors.phone?.message}
                />
                <p className="mt-1 text-xs text-text-light font-medium">
                  Enter the phone number linked to your active WhatsApp account.
                  Items cannot be approved without this.
                </p>
                {watchedPhone &&
                watchedPhone.replace(/\D/g, "").length >= 10 ? (
                  <p className="mt-2 text-xs text-text-light">
                    {nearMeWhatsappSignupDisclosureLine(watchedPhone)}
                  </p>
                ) : null}
              </div>

              <NormalSelect
                id="gender"
                label="Gender"
                value={watchedGender}
                options={[
                  { value: "Male", label: "Male" },
                  { value: "Female", label: "Female" },
                ]}
                placeholder="Select gender"
                {...(() => {
                  const { onChange, onBlur, name, ref } = register(
                    "gender",
                    signupValidationRules.gender,
                  );
                  return {
                    name,
                    onBlur,
                    ref,
                    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChange(e);
                      setValue("gender", e.target.value, {
                        shouldValidate: true,
                      });
                    },
                  };
                })()}
                error={errors.gender?.message}
                required
              />

              <div>
                <label
                  htmlFor="location"
                  className={cn(
                    "block text-sm font-medium text-foreground mb-1",
                    errors.location && "text-red-700",
                  )}
                >
                  Location <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 mb-2 text-sm text-text-light">
                  Please select your specific{" "}
                  <span className="font-bold">state</span> and{" "}
                  <span className="font-bold">neighborhood</span> for a better
                  experience finding items near you
                </p>
                <HierarchicalLocationSelect
                  value={watchedLocation}
                  onChange={handleLocationChange}
                  placeholder="Select your location"
                  className={cn("w-full", errors.location && "border-red-500")}
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.location.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  label="Password *"
                  placeholder="Enter your password"
                  {...register("password", signupValidationRules.password)}
                  error={errors.password?.message}
                />

                {/* Use the new password strength meter component */}
                <PasswordStrengthMeter
                  password={watchedPassword}
                  onStrengthChange={handlePasswordStrengthChange}
                />
              </div>

              <PasswordInput
                id="confirmPassword"
                autoComplete="new-password"
                label="Confirm password *"
                placeholder="Confirm your password"
                {...register("confirmPassword", {
                  ...signupValidationRules.confirmPassword,
                  validate: (value) => {
                    if (value !== watchedPassword) {
                      return "Passwords do not match";
                    }
                    return true;
                  },
                })}
                error={errors.confirmPassword?.message}
              />

              <NormalSelect
                id="discoveryChannel"
                label="How did you discover Sell It Off?"
                value={watchedDiscoveryChannel}
                options={[
                  { value: "chatgpt", label: "ChatGPT Conversation" },
                  { value: "friend", label: "Friend recommended Sell It Off" },
                  { value: "whatsapp", label: "Friend's WhatsApp Status" },
                  { value: "google", label: "Google Search" },
                  {
                    value: "market_partner",
                    label: "Market Partner (input their code below)",
                  },
                  {
                    value: "social-media",
                    label: "Sell It Off's LinkedIn/Instagram/Facebook pages",
                  },
                  { value: "other", label: "Other" },
                ]}
                placeholder="Select how you discovered Sell It Off"
                {...register("discoveryChannel", {
                  ...signupValidationRules.discoveryChannel,
                  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = e.target.value;
                    setValue("discoveryChannel", value, {
                      shouldValidate: true,
                    });
                    if (!isPartnerLocked && value !== "market_partner") {
                      setResolvedPartner(null);
                      setPartnerLookupError(null);
                      setValue("partnerReferralCode", "", {
                        shouldValidate: true,
                      });
                    }
                  },
                })}
                error={errors.discoveryChannel?.message}
                disabled={isPartnerLocked}
              />
              {shouldShowPartnerSection && (
                <div className="mt-3 rounded-lg border border-border p-3">
                  <Input
                    id="partnerReferralCode"
                    type="text"
                    label="Market partner code"
                    placeholder="Enter partner code"
                    disabled={isPartnerLocked}
                    {...register("partnerReferralCode", {
                      onChange: (e) => {
                        const sanitizedCode = sanitizePartnerCodeInput(
                          e.target.value,
                        );
                        if (sanitizedCode.length < 10) {
                          setResolvedPartner(null);
                          setPartnerLookupError(null);
                        }
                        setValue("partnerReferralCode", sanitizedCode, {
                          shouldValidate: true,
                        });
                      },
                    })}
                    error={partnerLookupError ?? undefined}
                  />
                  <p className="mt-1 text-xs text-text-light">
                    {isPartnerLocked
                      ? "Partner from referral link is locked for this signup."
                      : "We will verify this code and show the partner name below."}
                  </p>
                  {isPartnerLookupLoading && (
                    <p className="mt-1 text-xs text-text-light">
                      Verifying partner code...
                    </p>
                  )}
                  {resolvedPartner && (
                    <p className="mt-1 text-xs text-primary-700">
                      Partner: {resolvedPartner.name}
                    </p>
                  )}
                </div>
              )}
            </div>

            <TurnstileWidget onTokenChange={setCaptchaToken} />

            <div className="space-y-4">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
                disabled={!isFormValid || isLoading}
                haptic="light"
              >
                {isLoading ? "Creating account..." : "Create account"}
              </Button>

              {/* Show missing/invalid fields if button is disabled */}
              {!isFormValid && !isLoading && missingFields.length > 0 && (
                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <div className="font-semibold mb-1">
                    To create your account, please:
                  </div>
                  <ul className="list-disc list-inside space-y-1">
                    {missingFields.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-center">
                <p className="text-sm text-center text-text-light">Or</p>
              </div>

              <Button
                href="/login"
                type="button"
                className="w-full"
                variant="secondary"
                size="lg"
              >
                Log in
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Modal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Confirm Your Information"
        renderAction={true}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Yes, Create Account"
        cancelText="Go Back"
        isSubmitting={isLoading}
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Please review your information before creating your account:
          </p>
          <div className="bg-secondary p-4 rounded-lg space-y-2 text-sm">
            <div>
              <span className="font-medium">Name:</span> {watchedName}
            </div>
            <div>
              <span className="font-medium">Email:</span> {watchedEmail}
            </div>
            <div>
              <span className="font-medium">Phone:</span> {watchedPhone}
            </div>
            <div>
              <span className="font-medium">Location:</span> {watchedLocation}
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800 font-medium">
              ⚠️ Important: Please verify your WhatsApp number is correct.
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              We use this number to approve your item listings. If we cannot
              reach you on WhatsApp, your items will not be approved.
            </p>
          </div>
          <p className="text-xs text-text-light">
            By clicking "Yes, Create Account", you confirm that all information
            is correct.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default function SignUpPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SignUpContent />
    </Suspense>
  );
}
